import { computeTalkIdFromTalkData } from '../../shared/cid';
import type { GPSCoordinate, User } from '../../shared/types';
import { getAnsweredTalkByContent } from './answer-preferences-storage';
import { getFlatAnswerHistory, getTalkContentKey } from './answer-history-storage';
import { resolveExpiresAtMs } from './broadcast-audience-preview';
import type { CreatorReplyRow } from './creator-replies-view';
import { getMyTalks } from './my-talks-storage';
import { uncopyIncomingTalk } from './quick-incoming-talk-actions';
import { avatarInnerHtml } from './profile-avatar';
import { renderListProgressively } from './render-list-progressively';
import { getPinnedIds, pinnedFirst, toggleListItemPin } from './list-pins';
import { filterIncomingTalkClusters, getTalkIntakeFilters } from './talk-intake-filters';
import { formatTalkExpiryTone, getIncomingQuestionCount } from './talk-list-metadata';
import { escapeHtml, tagAnswerSuffix } from './ui-formatters';
import { flyTagChips, starterChipsScrolledPast, starterGroupHeadings } from './tag-chip-fly';
import {
  clearTalkRowSelection,
  flowTagChips,
  getSelectedTalkRow,
  reapplyTalkRowSelection,
  toolbarActionApplies,
  type TalkToolbarAction,
} from './tag-tap-mode';
import { STARTER_TAGS_CONTACT_ID } from '../../shared/starter-tags';
import type { UiTranslationKey } from './ui-translations';
import { answersLinkHtml, peopleLinkHtml, peopleWhoExchanged, bindGraphLinks, clearTalksFocus, getTalksFocus, peopleWhoLike, placeLinkHtml, placesOf, syncFocusChip, tagFaceHtml, tagText } from './graph-focus';
import { settingsStorage } from '../services/core-storage';

const TALKS_TAB_STATE_KEY = 'iinpublic_talks_tab_state';
const TALKS_FIRST_CHUNK_SIZE = 25;

type TalkStatsMap = Record<string, { responses: number; matches: number; ignores: number }>;
type PersonTarget = { type: 'person'; id: string; name: string };

type TalksListDocumentState = {
  renderSeq: number;
  mousedownDelegationBound: boolean;
  broadcastCheckboxBound: boolean;
  clickDelegationBound: boolean;
  latestDeps?: DisplayTalksListDeps;
};

const documentStates = new WeakMap<Document, TalksListDocumentState>();

function getDocumentState(): TalksListDocumentState {
  let state = documentStates.get(document);
  if (!state) {
    state = {
      renderSeq: 0,
      mousedownDelegationBound: false,
      broadcastCheckboxBound: false,
      clickDelegationBound: false,
    };
    documentStates.set(document, state);
  }
  return state;
}

export type DisplayTalksListDeps = {
  currentUser: User | undefined;
  currentLocation: GPSCoordinate | undefined;
  creatorReplyRows: CreatorReplyRow[];
  incomingTalkClusters: any[];
  talkStatsMap: TalkStatsMap;
  talksShowIncoming: boolean;
  talksShowOutgoing: boolean;
  talksEnabledTypes: Set<string>;
  talksOutSortMode: 'recent' | 'oldest' | 'latest-reply' | 'matches' | 'responses' | 'match-rate' | 'weighted' | 'title';
  talksQuery: string;
  talksCompletionFilter: 'all' | 'unanswered' | 'answered' | 'ignored';
  talksOutcomeFilter: 'all' | 'match' | 'mismatch';
  talksDateFrom: string;
  talksDateTo: string;
  syncStatusBarMatchCount: () => void;
  deleteMyTalk: (talkId: string) => void;
  restoreIgnoredTalk: (talkId: string) => void;
  quickAnswerIncomingTag: (talkId: string, identityKey: string | undefined, checked: boolean) => void;
  quickIgnoreIncomingTalk: (talkId: string, identityKey: string | undefined) => void;
  quickCopyIncomingTalk: (talkId: string, identityKey: string | undefined) => void;
  showTalkDetail: (talkId: string, identityKey?: string) => void;
  showSurveyStatsDialog: (talkId: string) => void;
  showCreatorRepliesForTalk: (talkId: string, talkTitle: string) => void;
  setTalkDisabled: (talkId: string, disabled: boolean) => void;
  showNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  t: (key: UiTranslationKey) => string;
  tf: (key: UiTranslationKey, values: Record<string, string | number>) => string;
  bindTalksRowGestures: () => void;
  getMyConversations: () => Record<string, any>;
  formatReasonCounts: (counts: Record<string, number>) => string;
  displayContextualStatistics: (elementId: string, prefix?: string) => void;
  getCoExchangedPeople: (identityKey: string, excludePeerIds: Set<string>) => Array<{ id: string; name: string }>;
  formatTalkDistanceFromAuthor: (location: { latitude?: number; longitude?: number } | null | undefined) => string;
  formatTalkExpiration: (expiresAt: number | null | undefined) => string;
  formatTalkLanguage: (code: string) => string;
  formatTalkLocation: (radiusMiles: number | null | undefined) => string;
  formatTalkRelativeTime: (date: Date) => string;
  formatTalkType: (type: string) => string;
  getIncomingResponseCount: (talkId: string) => number;
  getPreferredTalkLanguage: () => string;
  pickIncomingRowTalkId: (cluster: any) => string;
  showTalkEditorDialog: (existingTalk?: any) => void;
  showTalkTemplatePicker: () => void;
  navigateToGraphNode: (target: PersonTarget) => void;
  showChooseWhoToDmPicker: (people: Array<{ id: string; name: string }>) => void;
  emit: (event: string, payload: unknown) => unknown;
  persistTalksTabState: () => void;
  getTalksGestureSuppressClickUntil: () => number;
};

/** Owns Talks-tab filtering, rendering, delegated events, and progressive-render lifecycle. */
/**
 * What a tag row does (owner design 2026-10-24). `tap` is a plain tap: an incoming tag is liked
 * and moves to Outgoing (liking a starter tag also passes the starter tags scrolled past above it),
 * a liked tag in Outgoing is passed — the way back from a regretted Like — and a passed tag is
 * liked again; a tag I created ignores taps. `like` / `pass` come from the toolbar for a selected
 * row (Pass removes a tag I created).
 */
function actOnTagRow(chip: HTMLElement, intent: 'tap' | 'like' | 'pass', deps: DisplayTalksListDeps): void {
  if (chip.getAttribute('aria-busy') === 'true') return;
  const talkId = chip.dataset.talkId || '';
  const identityKey = chip.dataset.identityKey || undefined;
  const role = chip.dataset.role || '';
  if (!talkId) return;
  if (role === 'ignored') {
    if (intent !== 'pass') deps.quickAnswerIncomingTag(talkId, undefined, true);
    return;
  }
  if (role === 'copied' || role === 'created') {
    if (intent === 'like' || (intent === 'tap' && role === 'created')) return;
    flyTagChips([{ chip, direction: 'pass' }], () => {
      if (role === 'copied') deps.quickIgnoreIncomingTalk(talkId, undefined);
      else deps.deleteMyTalk(talkId);
    });
    return;
  }
  const like = intent !== 'pass';
  const passedAbove = like ? starterChipsScrolledPast(chip) : [];
  flyTagChips([
    ...passedAbove.map((above) => ({ chip: above, direction: 'pass' as const })),
    { chip, direction: like ? 'like' as const : 'pass' as const },
  ], () => {
    for (const above of passedAbove) deps.quickIgnoreIncomingTalk(above.dataset.talkId || '', above.dataset.identityKey || undefined);
    if (like) deps.quickAnswerIncomingTag(talkId, identityKey, true);
    else deps.quickIgnoreIncomingTalk(talkId, identityKey);
  });
}

/** A toolbar action on the long-press-selected row. */
function actOnSelectedRow(action: TalkToolbarAction, deps: DisplayTalksListDeps): void {
  const row = getSelectedTalkRow();
  if (!row || !toolbarActionApplies(action, row)) return;
  if (action === 'pin') {
    toggleListItemPin('talks', row.dataset.pinId || '');
    displayTalksList(deps);
    return;
  }
  clearTalkRowSelection();
  if (row.classList.contains('talk-tag-chip')) {
    actOnTagRow(row, action, deps);
    return;
  }
  // An incoming Flow/Survey/Route: Pass ignores it.
  deps.quickIgnoreIncomingTalk(row.dataset.talkId || '', row.dataset.identityKey || undefined);
}

export function displayTalksList(deps: DisplayTalksListDeps): void {
  bindGraphLinks();
  const documentState = getDocumentState();
  documentState.latestDeps = deps;
  const talksList = document.getElementById('talks-list');
  if (!talksList) return;
  const renderSeq = ++documentState.renderSeq;
  deps.syncStatusBarMatchCount();

  const myTalks = getMyTalks();
  const pinnedTalkIds = getPinnedIds('talks');
  const incomingPinKey = (cluster: any, talkId: string): string => `in:${String(cluster?.identityKey || talkId)}`;
  // Pinning is a toolbar mode (📌, tag-tap-mode.ts); a row shows a badge only while it is pinned.
  const pinButtonHtml = (pinKey: string): string => {
    if (!pinnedTalkIds.has(pinKey)) return '';
    const label = deps.t('unpinItem');
    return `<span class="talk-pin-badge" data-testid="talk-pin-badge" data-pin-id="${escapeHtml(pinKey)}" role="img" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}">📌</span>`;
  };
  // A non-tag talk is copied into My Talks (and so re-broadcast) only by the user's own choice:
  // this toggle, or dragging the row down. Same look as the pin; ✓ once copied.
  const copyButtonHtml = (talkId: string, identityKey: string): string => {
    const copied = myTalks[talkId]?.role === 'copied';
    const label = deps.t(copied ? 'talksRemoveCopy' : 'talksCopyToMyTalks');
    return `<button type="button" class="list-pin-button talk-copy-button ${copied ? 'is-copied' : ''}" data-testid="talk-copy-toggle" data-talk-id="${escapeHtml(talkId)}" data-identity-key="${escapeHtml(identityKey)}" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}" aria-pressed="${copied}">📋</button>`;
  };

  // One-time delegation on body: use mousedown so we run before any re-render can replace the DOM (click fires later and target can be gone)
  if (!documentState.mousedownDelegationBound) {
    documentState.mousedownDelegationBound = true;
    document.body.addEventListener(
      'mousedown',
      (e) => {
        if (e.button !== 0) return; // only left button
        const target = e.target as HTMLElement;
        // #item-details-popup: the long-press details popup relocates a row's
        // .talk-item-details out of #talks-list (showDetailsPopupFor) — its surviving
        // interactive content (survey-stats-btn) needs to still be reachable there.
        if (!target.closest('#talks-list') && !target.closest('#item-details-popup')) return;
        // Only reachable now from inside the long-press details popup (survey OUT rows).
        const surveyStatsBtn = target.closest('.survey-stats-btn');
        if (surveyStatsBtn) {
          e.preventDefault();
          e.stopPropagation();
          const talkId = (surveyStatsBtn as HTMLElement).dataset.talkId;
          if (talkId) {
            setTimeout(() => void deps.showSurveyStatsDialog(talkId), 0);
          }
          return;
        }
        // Only reachable from inside the long-press details popup (OUT rows with ≥1 response).
        const viewResponsesBtn = target.closest('.talk-view-responses-btn');
        if (viewResponsesBtn) {
          e.preventDefault();
          e.stopPropagation();
          const el = viewResponsesBtn as HTMLElement;
          const talkId = el.dataset.talkId || '';
          const talkTitle = el.dataset.talkTitle || '';
          if (talkId) {
            setTimeout(() => {
              // Close via the popup's own close button (not a raw .remove()) so it
              // properly returns .talk-item-details — this button's own container —
              // to its row first, instead of deleting it along with the modal.
              document.getElementById('close-item-details-popup')?.click();
              deps.showCreatorRepliesForTalk(talkId, talkTitle);
            }, 0);
          }
          return;
        }
      },
      { capture: true },
    );
  }
  // Broadcast on/off is now a real checkbox (same widget as the tag pill's own checkbox),
  // so it uses 'change' — not the mousedown-capture pattern above, which exists to hijack
  // custom-behavior elements before a native default (checked state, focus) applies. A
  // native checkbox's own toggle is exactly the behavior wanted here.
  if (!documentState.broadcastCheckboxBound) {
    documentState.broadcastCheckboxBound = true;
    document.body.addEventListener('change', (e) => {
      const checkbox = (e.target as HTMLElement).closest('.talk-broadcast-toggle-checkbox') as HTMLInputElement | null;
      if (!checkbox) return;
      const talkId = checkbox.dataset.talkId;
      if (!talkId) return;
      const disabled = !checkbox.checked;
      deps.setTalkDisabled(talkId, disabled);
      deps.showNotification(deps.t(disabled ? 'talksBroadcastDisabled' : 'talksBroadcastEnabled'), 'success');
    });
  }
  deps.bindTalksRowGestures();

  // Sort all talks by last interaction
  const allEntries = Object.entries(myTalks)
    .filter(([, talk]: [string, any]) => talk?.fullTalk?.practiceOnly !== true)
    .sort(
      ([, a]: [string, any], [, b]: [string, any]) =>
        new Date(b.lastInteraction || 0).getTime() - new Date(a.lastInteraction || 0).getTime(),
    );
  // OUT: talks this user created or copied (can broadcast)
  const conversations = deps.getMyConversations();
  const outMetrics = (talkId: string): {
    responses: number;
    matches: number;
    ignores: number;
    mismatches: number;
    matchRate: number;
    latestResponseAt: number;
    weighted: number;
  } => {
    const stats = deps.talkStatsMap[talkId];
    const replies = deps.creatorReplyRows.filter((reply) => reply.talkId === talkId);
    const derivedMatches = Object.values(conversations).filter((c: any) => c.talkId === talkId).length;
    const matches = Math.max(stats?.matches ?? 0, derivedMatches, replies.filter((reply) => reply.outcome === 'match').length);
    const responses = Math.max(stats?.responses ?? 0, derivedMatches, replies.length);
    const ignores = Math.max(stats?.ignores ?? 0, replies.filter((reply) => reply.outcome === 'ignore').length);
    const mismatches = Math.max(0, replies.filter((reply) => reply.outcome === 'mismatch').length || responses - matches - ignores);
    const matchRate = responses > 0 ? matches / responses : 0;
    const latestResponseAt = replies.reduce((latest, reply) => Math.max(latest, new Date(reply.date).getTime()), 0);
    // Visible factors only: matches dominate, then match rate and reply volume; ignores/mismatches lower rank.
    const weighted = matches * 100 + Math.round(matchRate * 25) + Math.min(responses, 20) - ignores * 4 - mismatches * 2;
    return { responses, matches, ignores, mismatches, matchRate, latestResponseAt, weighted };
  };
  // OUT: talks this user created or copied (can broadcast), with creator-selectable ranking.
  const outEntries = allEntries
    .filter(([, t]: [string, any]) => t.role === 'created' || t.role === 'copied')
    .sort(([idA, a]: [string, any], [idB, b]: [string, any]) => {
      const aa = outMetrics(idA);
      const bb = outMetrics(idB);
      if (deps.talksOutSortMode === 'oldest') return new Date(a.lastInteraction || 0).getTime() - new Date(b.lastInteraction || 0).getTime();
      if (deps.talksOutSortMode === 'latest-reply' && bb.latestResponseAt !== aa.latestResponseAt) return bb.latestResponseAt - aa.latestResponseAt;
      if (deps.talksOutSortMode === 'matches' && bb.matches !== aa.matches) return bb.matches - aa.matches;
      if (deps.talksOutSortMode === 'responses' && bb.responses !== aa.responses) return bb.responses - aa.responses;
      if (deps.talksOutSortMode === 'match-rate' && bb.matchRate !== aa.matchRate) return bb.matchRate - aa.matchRate;
      if (deps.talksOutSortMode === 'weighted' && bb.weighted !== aa.weighted) return bb.weighted - aa.weighted;
      if (deps.talksOutSortMode === 'title') return String(a.title || '').localeCompare(String(b.title || ''));
      return new Date(b.lastInteraction || 0).getTime() - new Date(a.lastInteraction || 0).getTime();
    });
  // IN: backend-consolidated incoming talks (content-hash merged)
  const rawIncomingEntries = (deps.incomingTalkClusters || []).filter((c: any) => c && c.identityKey);
  // Starter tags are chosen by the UI language already (OPEN-87); intake filters apply to people.
  const isStarter = (c: any) => c?.starterContactId === STARTER_TAGS_CONTACT_ID;
  const filteredIncoming = filterIncomingTalkClusters(
    rawIncomingEntries.filter((c: any) => !isStarter(c)),
    deps.currentUser?.talkFilters || getTalkIntakeFilters(),
    deps.currentLocation,
  );
  const incomingFilterResult = { ...filteredIncoming, visible: [...rawIncomingEntries.filter(isStarter), ...filteredIncoming.visible] };
  const hiddenReasonsText = deps.formatReasonCounts(incomingFilterResult.hiddenByReason);
  const answeredByContent = getAnsweredTalkByContent();
  const backendInEntries = incomingFilterResult.visible.filter((cluster: any) => {
    if (cluster?.isAnswered) return false;
    const identityKey = String(cluster?.identityKey || '');
    if (identityKey && answeredByContent[identityKey]) return false;
    try {
      const latestTalk = cluster?.latestTalk;
      if (latestTalk && answeredByContent[getTalkContentKey(latestTalk)]) return false;
      if (latestTalk && answeredByContent[computeTalkIdFromTalkData(latestTalk)]) return false;
    } catch {
      /* keep visible if the cluster cannot be locally identified */
    }
    return true;
  });
  // Answered talks are retained locally after they leave the actionable inbox. Put
  // them back in IN as read-only history so All/IN remains a complete talk ledger.
  const answeredIncomingEntries = allEntries
    // Completion and ownership are independent dimensions. Auto-save (or a manual Add
    // before answering) changes a received talk's role to `copied`, but it must still be
    // recoverable as read/answered IN history. Self-authored completions have no senders and
    // remain OUT-only.
    .filter(([, talk]: [string, any]) => {
      const hasCompletedAnswers = Array.isArray(talk?.completedAnswers) && talk.completedAnswers.length > 0;
      const hasSenders = Array.isArray(talk?.senders) && talk.senders.length > 0;
      const isAcceptedCopiedTag = String(talk?.type || talk?.fullTalk?.type || '').toLowerCase() === 'tag'
        && talk?.role === 'copied';
      return !isAcceptedCopiedTag && (talk?.role === 'answered' || (hasCompletedAnswers && hasSenders));
    })
    .map(([talkId, talk]: [string, any]) => {
      const fullTalk = talk?.fullTalk || {};
      const senderNames = Array.isArray(talk?.senders) ? talk.senders : [];
      const primaryName = String(senderNames[0] || talk?.senderName || deps.t('settingsUnknown'));
      return {
        identityKey: `answered:${talkId}`,
        title: String(talk?.title || fullTalk?.title || deps.t('talksIncomingFallback')),
        type: String(talk?.type || fullTalk?.type || 'flow'),
        language: String(fullTalk?.language || talk?.language || 'en'),
        latestTalk: fullTalk,
        senders: { [talkId]: { senderName: primaryName } },
        isAnswered: true,
        outcome: talk?.outcome,
        ...(talk?.answeredBy === 'chatbot' ? { answeredBy: 'chatbot' } : {}),
        questionCount: Array.isArray(fullTalk?.questions) ? fullTalk.questions.length : 0,
        updatedAt: talk?.lastInteraction || talk?.timestamp || Date.now(),
        expiresAt: fullTalk?.expiresAt ?? talk?.expiresAt,
        locationRadiusMiles: fullTalk?.locationRadiusMiles ?? talk?.locationRadiusMiles,
        latestTalkId: talkId,
      };
    });
  const ignoredIncomingEntries = allEntries
    .filter(([, talk]: [string, any]) => talk?.role === 'ignored')
    .map(([talkId, talk]: [string, any]) => {
      const fullTalk = talk?.fullTalk || {};
      const senderIds = Array.isArray(talk?.senders) ? talk.senders : [];
      const senders = Object.fromEntries(senderIds.map((senderId: string, index: number) => [
        `${talkId}:${index}`,
        { senderId, senderName: senderId },
      ]));
      return {
        identityKey: `ignored:${talkId}`,
        title: String(talk?.title || fullTalk?.title || deps.t('talksIncomingFallback')),
        type: String(talk?.type || fullTalk?.type || 'flow'),
        language: String(fullTalk?.language || talk?.language || 'en'),
        latestTalk: fullTalk,
        senders,
        isIgnored: true,
        isAnswered: false,
        outcome: talk?.outcome,
        questionCount: Array.isArray(fullTalk?.questions) ? fullTalk.questions.length : 0,
        updatedAt: talk?.lastInteraction || talk?.timestamp || Date.now(),
        expiresAt: fullTalk?.expiresAt ?? talk?.expiresAt,
        locationRadiusMiles: fullTalk?.locationRadiusMiles ?? talk?.locationRadiusMiles,
        latestTalkId: talkId,
      };
    });
  const allIncomingEntries = [...ignoredIncomingEntries, ...answeredIncomingEntries, ...backendInEntries];
  // A Me answer's 💬 (graph-focus.ts): only the Talks that asked that question.
  const focus = getTalksFocus();
  syncFocusChip('talks-focus-chip', focus?.label ?? null, deps.t, () => { clearTalksFocus(); displayTalksList(deps); });
  const focusIds = (entry: any, isIncoming: boolean): string[] => (isIncoming
    ? [entry?.latestTalkId, entry?.latestTalk?.id, String(entry?.identityKey || '').replace(/^ignored:/, ''), ...Object.keys(entry?.talkIds || {})]
    : [entry[0], entry[1]?.fullTalk?.id]).filter(Boolean).map(String);
  const matchesTalkFilter = (entry: any, isIncoming: boolean): boolean => {
    // Narrowed to a question's Talks: exactly those, whatever the other filters say (as Me does).
    if (focus) return focusIds(entry, isIncoming).some((id) => focus.talkIds.includes(id));
    const talk = isIncoming ? entry : entry[1];
    const type = String(talk?.type || talk?.fullTalk?.type || talk?.latestTalk?.type || 'flow').toLowerCase();
    const title = String(talk?.title || talk?.fullTalk?.title || talk?.latestTalk?.title || '').toLowerCase();
    const query = deps.talksQuery.trim().toLowerCase();
    const answered = isIncoming ? !!talk?.isAnswered : false;
    const ignored = isIncoming ? !!talk?.isIgnored : false;
    const outcome = String(talk?.outcome || talk?.latestTalk?.outcome || '').toLowerCase();
    const timestamp = new Date(talk?.updatedAt || talk?.lastInteraction || talk?.timestamp || 0).getTime();
    const from = deps.talksDateFrom ? new Date(`${deps.talksDateFrom}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
    const to = deps.talksDateTo ? new Date(`${deps.talksDateTo}T23:59:59.999`).getTime() : Number.POSITIVE_INFINITY;
    return (!query || title.includes(query))
      && deps.talksEnabledTypes.has(type)
      && (!isIncoming || deps.talksOutcomeFilter === 'all' || outcome === deps.talksOutcomeFilter)
      && timestamp >= from && timestamp <= to
      && (!isIncoming || (deps.talksCompletionFilter === 'ignored'
        ? ignored
        : !ignored && (deps.talksCompletionFilter === 'all'
          || (deps.talksCompletionFilter === 'answered' && answered)
          || (deps.talksCompletionFilter === 'unanswered' && !answered))));
  };
  const filteredOutEntries = deps.talksShowOutgoing || focus
    ? outEntries.filter((entry) => matchesTalkFilter(entry, false))
    : [];
  const inEntries = deps.talksShowIncoming || focus
    ? allIncomingEntries
        .filter((entry) => matchesTalkFilter(entry, true))
        .sort((a: any, b: any) => {
          if (a.isAnswered !== b.isAnswered) return a.isAnswered ? 1 : -1;
          if (deps.talksOutSortMode === 'title') return String(a.title || '').localeCompare(String(b.title || ''));
          if (deps.talksOutSortMode === 'oldest') return new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime();
          return new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
        })
    : [];
  // One combined summary line instead of two (app-bar direction counts + a separate
  // "Stats: ..." row below) — direction counts prefix the same response/match line.
  deps.displayContextualStatistics(
    'talks-stats-strip',
    deps.tf('talksStatusSummary', { incoming: inEntries.length, outgoing: filteredOutEntries.length }) + ' · ',
  );
  const talksIncomingCheckbox = document.getElementById('talks-filter-incoming') as HTMLInputElement | null;
  if (talksIncomingCheckbox) talksIncomingCheckbox.checked = deps.talksShowIncoming;
  const talksOutgoingCheckbox = document.getElementById('talks-filter-outgoing') as HTMLInputElement | null;
  if (talksOutgoingCheckbox) talksOutgoingCheckbox.checked = deps.talksShowOutgoing;
  document.querySelectorAll<HTMLInputElement>('.talks-type-checkbox').forEach((checkbox) => {
    checkbox.checked = deps.talksEnabledTypes.has(checkbox.value);
  });
  const talksSort = document.getElementById('talks-out-sort-order') as HTMLSelectElement | null;
  if (talksSort) talksSort.value = deps.talksOutSortMode;
  const talksQuery = document.getElementById('talks-filter-query') as HTMLInputElement | null;
  if (talksQuery && talksQuery.value !== deps.talksQuery) talksQuery.value = deps.talksQuery;
  const talksCompletionFilter = document.getElementById('talks-filter-completion') as HTMLSelectElement | null;
  if (talksCompletionFilter) talksCompletionFilter.value = deps.talksCompletionFilter;
  const talksOutcomeFilter = document.getElementById('talks-filter-outcome') as HTMLSelectElement | null;
  if (talksOutcomeFilter) talksOutcomeFilter.value = deps.talksOutcomeFilter;
  const talksDateFrom = document.getElementById('talks-filter-date-from') as HTMLInputElement | null;
  const talksDateTo = document.getElementById('talks-filter-date-to') as HTMLInputElement | null;
  if (talksDateFrom) talksDateFrom.value = deps.talksDateFrom;
  if (talksDateTo) talksDateTo.value = deps.talksDateTo;

  if (filteredOutEntries.length === 0 && inEntries.length === 0) {
    const hasTalkHistory = allEntries.length > 0 || rawIncomingEntries.length > 0;
    talksList.innerHTML = `
      <div class="empty-state" style="padding: 60px 20px; text-align: center;">
        <div style="font-size: 3em; margin-bottom: 16px;">💬</div>
        <p style="font-size: 1.2em; color: #666; margin-bottom: 8px;">${deps.t('talksNoTalks')}</p>
        <p style="font-size: 0.9em; color: #999;">${deps.t(focus?.peerId ? 'mapTalksEmpty' : 'talksNoTalksHelp')}</p>
        ${hiddenReasonsText ? `<p style="font-size: 0.85em; color: #999; margin-top: 8px;">${escapeHtml(hiddenReasonsText)}</p>` : ''}
        ${!hasTalkHistory && !focus ? `<div class="talks-starter-actions">
          <button type="button" class="btn" id="talks-starter-more" data-testid="talks-starter-more">${escapeHtml(deps.t('talksStarterMore'))}</button>
          <button type="button" class="btn" id="talks-starter-scratch" data-testid="talks-starter-scratch">${escapeHtml(deps.t('talksStarterScratch'))}</button>
        </div>` : ''}
      </div>
    `;
    talksList.querySelector('#talks-starter-more')?.addEventListener('click', deps.showTalkTemplatePicker);
    talksList.querySelector('#talks-starter-scratch')?.addEventListener('click', () => deps.showTalkEditorDialog());
  } else {
    // TODO §R2: named so it can be passed to renderListProgressively as `renderRow`,
    // instead of an inline .map() callback over the entire list at once.
    const renderOutRow = ([talkId, talk]: [string, any]): string => {
                const stats = deps.talkStatsMap[talkId];
                const matchedPeople = Object.values(conversations)
                  .filter((c: any) => c.talkId === talkId && c.otherUserId)
                  .map((c: any) => ({
                    id: String(c.otherUserId),
                    name: c.respondedByBot ? `${c.otherUserName} 🤖` : String(c.otherUserName || ''),
                  }));
                const matchedNames = matchedPeople.map((p) => p.name);
                const metrics = outMetrics(talkId);
                const statsLine = stats || metrics.responses > 0
                  ? deps.tf('talksStats', {
                      responses: metrics.responses,
                      matches: metrics.matches,
                      mismatches: metrics.mismatches,
                      ignores: metrics.ignores,
                      rate: Math.round(metrics.matchRate * 100),
                    })
                  : deps.t('talksNoStats');
                const rankLine = deps.talksOutSortMode === 'weighted'
                  ? `<div class="talk-weighted-score" style="font-size:0.82em;color:var(--text-tertiary);margin-top:4px;">${deps.tf('talksWeightedScore', { score: metrics.weighted })}</div>`
                  : deps.talksOutSortMode === 'latest-reply' && metrics.latestResponseAt > 0
                    ? `<div class="talk-weighted-score" style="font-size:0.82em;color:var(--text-tertiary);margin-top:4px;">${deps.tf('talksLatestReplyLabel', { date: escapeHtml(new Date(metrics.latestResponseAt).toLocaleString()) })}</div>`
                    : '';
                const matchedLine =
                  matchedPeople.length > 0
                    ? `<div class="talk-item-matched talk-matched-people" data-matched-people="${escapeHtml(JSON.stringify(matchedPeople))}" style="font-size: 0.85em; color: var(--success-text); margin-top: 4px; cursor: pointer;">${deps.tf('talksMatchedWith', { names: escapeHtml(matchedNames.join(', ')) })}</div>`
                    : '';
                // TODO §Q build-order item 17: "people I've separately exchanged this same
                // content with" — a different talkId sharing the same identityKey (e.g. a
                // separate broadcast round), scoped to this device's own talkLedger only.
                // Excludes people already shown by matchedLine above (this row's own partners).
                const coExchangedPeople = talk.fullTalk
                  ? deps.getCoExchangedPeople(
                      computeTalkIdFromTalkData(talk.fullTalk),
                      new Set(matchedPeople.map((p) => p.id)),
                    )
                  : [];
                const coExchangedLine =
                  coExchangedPeople.length > 0
                    ? `<div class="talk-item-co-exchanged talk-matched-people" data-matched-people="${escapeHtml(JSON.stringify(coExchangedPeople))}" style="font-size: 0.85em; color: var(--accent-text); margin-top: 4px; cursor: pointer;">${deps.tf('talksAlsoExchangedWith', { names: escapeHtml(coExchangedPeople.map((p) => p.name).join(', ')) })}</div>`
                    : '';
                const disabled = !!talk.disabled;
                const expText = deps.formatTalkExpiration(talk.expiresAt);
                const locText = deps.formatTalkLocation(talk.locationRadiusMiles);
                // TODO §Z popup-variant review: match the IN details popup's tone-colored
                // expiry chip (formatTalkExpiryTone) instead of OUT's own plain, uncolored
                // text — my own sent talks approaching expiry deserve the same at-a-glance
                // urgency cue an incoming talk's expiry already gets.
                const expiryTone = formatTalkExpiryTone(talk.expiresAt);
                // Icon-only badges, not text: direction/copy-state and type are already
                // conveyed by shape (this icon) and color (typeAccent border) — a text
                // label alongside both would just repeat the same fact in words. The
                // translated label still exists for a11y/tooltip/screen-reader purposes.
                const roleBadge = talk.role === 'copied'
                  ? `<span class="talk-badge talk-badge-copied" title="${escapeHtml(deps.t('talksCopied'))}" style="background:var(--accent-soft);color:var(--accent-text);">📋<span class="visually-hidden"> ${deps.t('talksCopied')}</span></span>`
                  : `<span class="talk-badge talk-badge-created" title="${escapeHtml(deps.t('talksCreated'))}" style="background:var(--accent-soft);color:var(--accent-text);">📝<span class="visually-hidden"> ${deps.t('talksCreated')}</span></span>`;
                // A received talk the chatbot answered (and auto-copy saved here): same 🤖 record.
                const outByChatbot = talk.role === 'copied' && talk.answeredBy === 'chatbot';
                const outChatbotBadge = outByChatbot
                  ? `<span class="talk-badge talk-badge-chatbot" data-testid="talk-answered-by-chatbot" title="${escapeHtml(deps.t('talksAnsweredByChatbot'))}">🤖<span class="visually-hidden"> ${escapeHtml(deps.t('talksAnsweredByChatbot'))}</span></span>`
                  : '';
                const talkTypeLower = String(talk.type || talk.fullTalk?.type || '').toLowerCase();
                const talkLanguage = String(talk.language || talk.fullTalk?.language || 'en').toLowerCase();
                const typeAccent =
                  talkTypeLower === 'tag' ? '#7c3aed'
                  : talkTypeLower === 'survey' ? 'var(--success)'
                  : talkTypeLower === 'route' ? '#d97706'
                  : 'var(--accent)';
                const typeIcon =
                  talkTypeLower === 'tag' ? '🏷️'
                  : talkTypeLower === 'survey' ? '📊'
                  : talkTypeLower === 'route' ? '🔀'
                  : '➡️';
                if (talkTypeLower === 'tag') {
                  return `
      <div class="talk-list-item talk-tag-chip talk-tag-out is-liked ${disabled ? 'talk-broadcast-disabled' : 'talk-broadcast-enabled'}" data-talk-id="${talkId}" data-pin-id="${escapeHtml(`out:${talkId}`)}" data-role="${talk.role || 'created'}" data-talk-type="tag" role="button" tabindex="0" aria-label="${escapeHtml(`${deps.t('talksTagLiked')}: ${talk.title || ''}`)}">
        ${tagFace({ state: 'liked', talk, talkId, suffixHtml: tagAnswerSuffix(talk) })}
        ${pinButtonHtml(`out:${talkId}`)}
      </div>
    `;
                }
                // Row is a single tap target (opens the editor) plus a gesture, not a row of
                // buttons: the checkbox in the top-left badge is the one persistent explicit
                // control (broadcast on/off — same widget as the tag pill's own checkbox, so
                // both "is this actively going out" toggles look and feel the same); everything
                // else that used to be a button moved to a different mechanism — 🗑️ delete ->
                // swipe-left gesture, ℹ️ details -> long-press (still the exact same
                // .talk-item-details/showDetailsPopupFor content, nothing dropped), 📊 survey
                // results -> the at-a-glance number now lives in the stats line, with the full
                // breakdown dashboard one tap away inside that same long-press popup instead of
                // its own row button. matchedLine stays visible on the row: it's the interactive
                // N3 click-to-DM affordance, not decorative detail.
                return `
      <div class="talk-list-item talk-direction-out talk-type-${escapeHtml(talkTypeLower || 'flow')} ${disabled ? 'talk-broadcast-disabled' : 'talk-broadcast-enabled'}${outByChatbot ? ' talk-answered-by-chatbot' : ''}" data-talk-id="${talkId}" data-pin-id="${escapeHtml(`out:${talkId}`)}" data-role="${talk.role || 'created'}" data-talk-type="${escapeHtml(talkTypeLower || 'flow')}"${outByChatbot ? ' data-answered-by="chatbot"' : ''} style="border-right:5px solid ${outByChatbot ? 'var(--chatbot-accent, #7c5cd6)' : typeAccent};background:${outByChatbot ? 'var(--chatbot-soft, #f1ecfb)' : 'var(--surface)'};">
        <div class="talk-item-header">
          <label class="talk-icon-badge" title="${disabled ? deps.t('talksBroadcastOff') : deps.t('talksBroadcastOn')}">
            <input type="checkbox" class="talk-broadcast-toggle-checkbox" data-talk-id="${talkId}" ${disabled ? '' : 'checked'}>
            <span aria-hidden="true">${typeIcon}</span>
          </label>
          ${outChatbotBadge}
          <div class="talk-item-title">${escapeHtml(talk.title)}${tagAnswerSuffix(talk)}</div>
          ${pinButtonHtml(`out:${talkId}`)}
          <span class="talk-item-chevron" aria-hidden="true">›</span>
        </div>
        <div class="talk-item-status-line" style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:4px;">
          <span class="talk-item-status-summary" style="font-size:0.85em;color:#666;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(statsLine)} · ${escapeHtml(deps.formatTalkRelativeTime(new Date(talk.lastInteraction || 0)))}</span>
          ${talkGraphLinks(talkId, talk.title)}
        </div>
        ${matchedLine}
        <div class="talk-item-details" data-talk-id="${talkId}" style="display:none;">
          ${roleBadge}
          <span class="talk-badge talk-badge-type" title="${escapeHtml(deps.formatTalkType(String(talk.type || 'flow')))}">${typeIcon}<span class="visually-hidden"> ${deps.formatTalkType(String(talk.type || 'flow'))}</span></span>
          <span class="talk-badge talk-badge-language" data-language="${escapeHtml(talkLanguage)}">${escapeHtml(deps.formatTalkLanguage(talkLanguage))}</span>
          <div class="talk-item-meta">
            <span class="talk-item-time">${deps.formatTalkRelativeTime(new Date(talk.lastInteraction || 0))}</span>
          </div>
          <div class="talk-info-chips">
            <span class="talk-info-chip talk-expiry-${expiryTone}">${escapeHtml(deps.tf('talksExpiration', { value: expText }))}</span>
            <span class="talk-info-chip">${escapeHtml(deps.tf('talksLocation', { value: locText }))}</span>
          </div>
          <div class="talk-item-stats" style="font-size: 0.85em; color: #666; margin-top: 6px;">
            ${statsLine}
          </div>
          ${rankLine}
          ${talkTypeLower === 'survey' ? `<button type="button" class="btn survey-stats-btn talk-icon-btn" data-talk-id="${escapeHtml(talkId)}" data-testid="survey-stats-button" style="margin-top:6px;color:var(--accent-text);">📊 ${deps.t('talksResults')}</button>` : ''}
          ${metrics.responses > 0 ? `<button type="button" class="btn talk-view-responses-btn talk-icon-btn" data-talk-id="${escapeHtml(talkId)}" data-talk-title="${escapeHtml(talk.title)}" data-testid="talk-view-responses-button" style="margin-top:6px;color:var(--accent-text);">👥 ${escapeHtml(deps.tf('talksViewResponses', { count: metrics.responses }))}</button>` : ''}
          ${coExchangedLine}
        </div>
      </div>
    `;
    };

    // TODO §R2: named so it can be passed to renderListProgressively as `renderRow`.
    // OPEN-87: starter tags sit under their group heading (icon + label), set after sorting below.
    let starterHeadings = new Map<string, { id: string; icon: string; label: string }>();
    let lastStarterKey = '';
    const renderInRow = (cluster: any): string => {
              const sendersObj = cluster?.senders && typeof cluster.senders === 'object' ? cluster.senders : {};
              const senderNames = Array.from(
                new Set(
                  Object.values(sendersObj)
                    .map((s: any) => String(s?.senderName || '').trim())
                    .filter(Boolean),
                ),
              );
              const senderList = Object.values(sendersObj) as Array<{ senderId?: string; senderName?: string; headshot?: string }>;
              const primarySender = senderList[0] || {};
              const primarySenderName = String(primarySender.senderName || senderNames[0] || deps.t('settingsUnknown'));
              const senderPeopleById = new Map<string, string>();
              for (const s of senderList) {
                if (s?.senderId && s?.senderName) senderPeopleById.set(String(s.senderId), String(s.senderName));
              }
              const senderPeople = Array.from(senderPeopleById, ([id, name]) => ({ id, name }));
              const senderPeopleJson = escapeHtml(JSON.stringify(senderPeople));
              const senderInitial = primarySenderName.trim().charAt(0).toUpperCase() || '?';
              const talkId = deps.pickIncomingRowTalkId(cluster);
              const identityKey = String(cluster?.identityKey || '');
              const pinKey = incomingPinKey(cluster, talkId);
              // TODO §Q build-order item 17: other people I've separately exchanged this same
              // content with (e.g. a different sender who sent me the identical talk), scoped
              // to this device's own talkLedger only. Excludes this cluster's own sender(s).
              const coExchangedPeople = identityKey
                ? deps.getCoExchangedPeople(identityKey, new Set(senderPeople.map((p) => p.id)))
                : [];
              const coExchangedLine =
                coExchangedPeople.length > 0
                  ? `<div class="talk-item-co-exchanged talk-matched-people" data-matched-people="${escapeHtml(JSON.stringify(coExchangedPeople))}" style="font-size: 0.85em; color: var(--accent-text); margin-top: 4px; cursor: pointer;">${deps.tf('talksAlsoExchangedWith', { names: escapeHtml(coExchangedPeople.map((p) => p.name).join(', ')) })}</div>`
                  : '';
              const isAnswered = !!cluster?.isAnswered;
              const isIgnored = !!cluster?.isIgnored;
              const titleStyle = isAnswered
                ? 'font-weight: 500; color: var(--text-muted);'
                : 'font-weight: 700; color: var(--accent-hover);';
              const metaStyle = isAnswered ? 'color: var(--text-muted);' : 'color: var(--text-tertiary);';
              const statusBadge = isAnswered
                ? `<span class="talk-badge" style="background:var(--bg-muted);color:var(--text-tertiary);">✅ ${deps.t('talksAnswered')}</span>`
                : `<span class="talk-badge" style="background:var(--accent-soft);color:var(--accent-hover);font-weight:700;">🆕 ${deps.t('talksNew')}</span>`;
              const incomingType = String(cluster?.type || 'flow').toLowerCase();
              const incomingLanguage = String(cluster?.language || cluster?.latestTalk?.language || 'en').toLowerCase();
              const questionCount = getIncomingQuestionCount(cluster);
              const responseCount = deps.getIncomingResponseCount(talkId);
              const expiresAt = cluster?.expiresAt ?? cluster?.latestTalk?.expiresAt;
              const expiryTone = formatTalkExpiryTone(expiresAt);
              const expText = deps.formatTalkExpiration(Number.isFinite(resolveExpiresAtMs(expiresAt)) ? resolveExpiresAtMs(expiresAt) : null);
              const locRadius = cluster?.locationRadiusMiles ?? cluster?.latestTalk?.locationRadiusMiles;
              const locText = deps.formatTalkLocation(locRadius);
              const distanceText = deps.formatTalkDistanceFromAuthor(cluster?.authorLocation || cluster?.latestTalk?.authorLocation);
              const showLanguageBadge = incomingLanguage && incomingLanguage !== deps.getPreferredTalkLanguage();
              const progressChip = (incomingType === 'flow' || incomingType === 'route') && questionCount > 0
                ? `<span class="talk-info-chip talk-progress-chip" style="--progress: 0%;"><span class="talk-progress-ring" aria-hidden="true"></span>${escapeHtml(`Q1/${questionCount}`)}</span>`
                : questionCount > 0
                  ? `<span class="talk-info-chip">${escapeHtml(`${questionCount} Q`)}</span>`
                  : '';
              const languageChip = showLanguageBadge
                ? `<span class="talk-info-chip talk-language-alert">${escapeHtml(deps.formatTalkLanguage(incomingLanguage))}</span>`
                : '';
              const responseChip = responseCount > 0
                ? `<span class="talk-info-chip">${escapeHtml(deps.tf(responseCount === 1 ? 'talksResponseOne' : 'talksResponses', { count: responseCount }))}</span>`
                : '';
              const distanceChip = distanceText
                ? `<span class="talk-info-chip">${escapeHtml(distanceText)}</span>`
                : '';
              const typeAccent =
                incomingType === 'tag' ? '#7c3aed'
                : incomingType === 'survey' ? 'var(--success)'
                : incomingType === 'route' ? '#d97706'
                : 'var(--accent)';
              const typeIcon =
                incomingType === 'tag' ? '🏷️'
                : incomingType === 'survey' ? '📊'
                : incomingType === 'route' ? '🔀'
                : '➡️';
              if (isIgnored) {
                if (incomingType === 'tag') {
                  return `
      <div class="talk-list-item talk-tag-chip talk-tag-passed talk-ignored-row" data-talk-id="${escapeHtml(talkId)}" data-role="ignored" data-incoming-type="tag" role="button" tabindex="0" aria-label="${escapeHtml(`${deps.t('talksTagPassed')}: ${cluster?.title || ''}`)}">
        ${tagFace({ state: 'passed', talk: cluster?.latestTalk || cluster, talkId: String(talkId) })}
      </div>
    `;
                }
                return `
      <div class="talk-list-item talk-direction-in talk-type-${escapeHtml(incomingType)} talk-ignored-row" data-talk-id="${escapeHtml(talkId)}" data-role="ignored" data-incoming-type="${escapeHtml(incomingType)}" style="border-left:5px solid ${typeAccent};background:var(--surface);">
        <div class="talk-item-header">
          <span class="talk-icon-badge" aria-hidden="true">${typeIcon}</span>
          <span class="talk-item-title">${escapeHtml(cluster?.title || deps.t('talksIncomingFallback'))}</span>
          <button type="button" class="btn talk-restore-ignored-btn" data-talk-id="${escapeHtml(talkId)}">${escapeHtml(deps.t('talksRestore'))}</button>
        </div>
      </div>
    `;
              }
              if (incomingType === 'tag') {
                const heading = starterHeadings.get(identityKey);
                return `${heading ? `
      <div class="starter-tag-group-heading" data-testid="starter-tag-group-heading" data-starter-group="${escapeHtml(heading.id)}"><span aria-hidden="true">${escapeHtml(heading.icon)}</span> ${escapeHtml(heading.label)}</div>` : ''}
      <div class="talk-list-item talk-tag-chip talk-tag-in ${isAnswered ? 'talk-incoming-answered' : 'talk-incoming-new'}" data-talk-id="${talkId}" data-pin-id="${escapeHtml(pinKey)}" data-identity-key="${escapeHtml(identityKey)}" data-role="incoming" data-incoming-type="tag"${cluster?.starterContactId === STARTER_TAGS_CONTACT_ID ? ' data-starter-tag="true"' : ''}${cluster?.starterGroup?.id ? ` data-starter-group="${escapeHtml(cluster.starterGroup.id)}"` : ''} data-pass-label="${escapeHtml(deps.t('talksTagPass'))}" data-like-label="${escapeHtml(deps.t('talksTagLike'))}" role="button" tabindex="0" aria-label="${escapeHtml(cluster?.title || deps.t('talksIncomingFallback'))}">
        ${tagFace({ state: 'open', talk: cluster?.latestTalk || cluster, talkId: String(talkId) })}
        ${pinButtonHtml(pinKey)}
      </div>${identityKey === lastStarterKey ? `
      <div class="starter-tag-create-prompt" data-testid="starter-tag-create-prompt">
        <div class="starter-tag-create-title">✨ ${escapeHtml(deps.t('starterCreatePromptTitle'))}</div>
        <div class="starter-tag-create-body">${escapeHtml(deps.t('starterCreatePromptBody'))}</div>
        <button type="button" class="btn primary-btn starter-tag-create-btn" data-testid="starter-tag-create-btn">${escapeHtml(deps.t('starterCreatePromptAction'))}</button>
      </div>` : ''}
    `;
              }
              // Row is a single tap target (opens the talk to answer, with the details below
              // already visible rather than a separate popup). 📋 or drag down retains it in My Talks,
              // a left swipe ignores this content; right swipe is intentionally reserved for
              // the complete one-tap Like answer available only to Tag/Pair-tag rows.
              // Long-press still reaches the exact
              // same .talk-item-details/showDetailsPopupFor content the ℹ️ button used to
              // (full sender identity + co-exchanged people), nothing dropped, just a different
              // trigger. Row 2 now carries what fit in the freed-up space: time, sender count,
              // location, question progress — the "quick glance" subset of the popup's fuller
              // detail set.
              const questionProgressText = (incomingType === 'flow' || incomingType === 'route') && questionCount > 0
                ? `Q1/${questionCount}`
                : questionCount > 0 ? `${questionCount} Q` : '';
              const senderCountText = senderNames.length > 1
                ? `👥 ${deps.tf('talksSenders', { count: senderNames.length })}`
                : `👤 ${deps.tf('talksSenderOne', { count: 1 })}`;
              const row2Parts = [
                deps.formatTalkRelativeTime(new Date(cluster?.updatedAt || Date.now())),
                senderCountText,
                `📍 ${locText}`,
                questionProgressText,
              ].filter(Boolean);
              // Answered automatically by the receiver's chatbot: a distinct 🤖 marker and tint so
              // the user has a record of it without having done anything; opening the row reviews
              // (and can re-answer) it like any answered talk.
              const byChatbot = isAnswered && cluster?.answeredBy === 'chatbot';
              const chatbotBadge = byChatbot
                ? `<span class="talk-badge talk-badge-chatbot" data-testid="talk-answered-by-chatbot" title="${escapeHtml(deps.t('talksAnsweredByChatbot'))}">🤖<span class="visually-hidden"> ${escapeHtml(deps.t('talksAnsweredByChatbot'))}</span></span>`
                : '';
              return `
      <div class="talk-list-item talk-direction-in talk-type-${escapeHtml(incomingType)} ${isAnswered ? 'talk-incoming-answered' : 'talk-incoming-new'}${byChatbot ? ' talk-answered-by-chatbot' : ''}" data-talk-id="${talkId}" data-pin-id="${escapeHtml(pinKey)}" data-identity-key="${escapeHtml(identityKey)}" data-role="incoming" data-incoming-type="${escapeHtml(incomingType)}"${byChatbot ? ' data-answered-by="chatbot"' : ''} data-ignore-label="${escapeHtml(deps.t('talksIgnore'))}" style="border-left:5px solid ${byChatbot ? 'var(--chatbot-accent, #7c5cd6)' : typeAccent};background:${byChatbot ? 'var(--chatbot-soft, #f1ecfb)' : 'var(--accent-soft)'};">
        <div class="talk-item-header">
          <span class="talk-icon-badge" title="${escapeHtml(deps.formatTalkType(String(cluster?.type || 'flow')))}" aria-hidden="true">📥 ${typeIcon}</span>
          ${chatbotBadge}
          <button type="button" class="talk-item-title view-talk-btn" data-talk-id="${talkId}" data-identity-key="${escapeHtml(identityKey)}" style="${titleStyle}background:none;border:none;padding:0;text-align:left;cursor:pointer;font:inherit;">${escapeHtml(cluster?.title || deps.t('talksIncomingFallback'))}</button>
          ${copyButtonHtml(talkId, identityKey)}
          ${pinButtonHtml(pinKey)}
          <span class="talk-item-chevron" aria-hidden="true">›</span>
        </div>
        <div class="talk-item-status-line" style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:4px;">
          <span class="talk-item-status-summary" style="${metaStyle}font-size:0.85em;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(row2Parts.join(' · '))}</span>
          ${talkGraphLinks(String(talkId), cluster?.title, identityKey)}
        </div>
        <div class="talk-item-details" data-talk-id="${talkId}" style="display:none;">
          ${statusBadge}
          <span class="talk-badge talk-badge-type" title="${escapeHtml(deps.formatTalkType(String(cluster?.type || 'flow')))}">${typeIcon}<span class="visually-hidden"> ${deps.formatTalkType(String(cluster?.type || 'flow'))}</span></span>
          <div class="talk-incoming-sender talk-sender-people" data-sender-people="${senderPeopleJson}" style="cursor:pointer;display:flex;align-items:center;gap:6px;margin-bottom:8px;margin-top:8px;">
            <span class="talk-incoming-avatar">${avatarInnerHtml(primarySender.headshot, senderInitial, escapeHtml)}</span>
            <span class="talk-incoming-sender-name">${escapeHtml(primarySenderName)}</span>
            ${senderNames.length > 1 ? `<span class="talk-info-chip">${deps.tf('talksSenders', { count: senderNames.length })}</span>` : ''}
          </div>
          <div class="talk-info-chips">
            ${progressChip}
            ${languageChip}
            <span class="talk-info-chip talk-expiry-${expiryTone}">${escapeHtml(expText)}</span>
            <span class="talk-info-chip">${escapeHtml(locText)}</span>
            ${distanceChip}
            ${responseChip}
          </div>
          <div class="talk-item-meta" style="${metaStyle}">
            <span class="talk-item-time">${deps.formatTalkRelativeTime(new Date(cluster?.updatedAt || Date.now()))}</span>
          </div>
          ${coExchangedLine}
        </div>
      </div>
    `;
    };

    // One face for a tag on Talks and Me (graph-focus.ts): ♡ / ✕ / none, its text, and "👥 N".
    const answeredTalkIds = new Set(Object.values(getFlatAnswerHistory()).map((record) => record.talkId));
    const talkGraphLinks = (talkId: string, title: string, identityKey?: string): string => {
      const ids = [...new Set([talkId, ...(identityKey ? [identityKey] : [])])];
      const linkDeps = { escapeHtml, text: deps.t, answeredTalkIds };
      return `<span class="talk-graph-links">${peopleLinkHtml(title, peopleWhoExchanged(ids, false).length, linkDeps, ids)}${answersLinkHtml(title, ids, linkDeps)}${placeLinkHtml(placesOf({ talkIds: ids }), linkDeps)}</span>`;
    };
    const tagFace = (face: { state: 'liked' | 'passed' | 'open'; talk: any; talkId: string; suffixHtml?: string }): string => {
      const questions = face.talk?.fullTalk?.questions || face.talk?.questions || [];
      const keyword = String(questions[0]?.text || '');
      const title = String(face.talk?.title || face.talk?.fullTalk?.title || deps.t('talksIncomingFallback'));
      // A Pair tag or a many-keyword tag reads by its title; a one-keyword tag like its Me row.
      const simple = questions.length === 1 && !questions[0]?.reciprocalTagContext;
      const people = face.state === 'open' ? 0 : peopleWhoLike([face.talkId]).length;
      return tagFaceHtml({
        state: face.state,
        text: simple ? tagText(title, keyword) : title,
        topic: keyword || title,
        people,
        talkIds: [face.talkId],
        // On Talks, the ♡ / ✕ is my answer: it opens that answer on Me.
        markLinksToAnswer: true,
        ...(face.suffixHtml ? { suffixHtml: face.suffixHtml } : {}),
      }, { escapeHtml, text: deps.t }) + placeLinkHtml(placesOf({ talkIds: [face.talkId] }), { escapeHtml, text: deps.t });
    };

    const isStale = () => renderSeq !== documentState.renderSeq;

    // One merged, chronologically-sorted list — like an email inbox, not two
    // color (type accent) and icon (direction), so a section header would be
    // redundant wording on top of that. When only one direction is checked, the
    // richer OUT-specific sort modes (matches/responses/weighted/...) still apply;
    // mixing both directions together only makes sense sorted by recency.
    type MergedTalkRow = { direction: 'in' | 'out' | 'divider'; sortTime: number; needsAnswer: boolean; pinKey: string; payload: any };
    const outRows: MergedTalkRow[] = filteredOutEntries.map(([id, talk]: [string, any]) => ({
      direction: 'out' as const,
      sortTime: new Date(talk.lastInteraction || 0).getTime(),
      needsAnswer: false,
      pinKey: `out:${id}`,
      payload: [id, talk] as [string, any],
    }));
    const inRows: MergedTalkRow[] = inEntries.map((cluster: any) => ({
      direction: 'in' as const,
      sortTime: new Date(cluster?.updatedAt || 0).getTime(),
      needsAnswer: !cluster?.isAnswered,
      pinKey: incomingPinKey(cluster, deps.pickIncomingRowTalkId(cluster)),
      payload: cluster,
    }));
    // Incoming first (unanswered on top, then recency), then a divider, then outgoing (owner,
    // 2026-10-24); pins float to the top of their own section.
    const byActionThenRecency = (a: MergedTalkRow, b: MergedTalkRow): number =>
      (a.needsAnswer !== b.needsAnswer ? (a.needsAnswer ? -1 : 1) : b.sortTime - a.sortTime);
    const inSection = pinnedFirst(deps.talksShowIncoming && deps.talksShowOutgoing ? [...inRows].sort(byActionThenRecency) : inRows, 'talks', (row) => row.pinKey);
    const outSection = pinnedFirst(deps.talksShowIncoming && deps.talksShowOutgoing ? [...outRows].sort(byActionThenRecency) : outRows, 'talks', (row) => row.pinKey);
    const divider: MergedTalkRow = { direction: 'divider', sortTime: 0, needsAnswer: false, pinKey: '', payload: null };
    const mergedRows = inSection.length > 0 && outSection.length > 0
      ? [...inSection, divider, ...outSection]
      : [...inSection, ...outSection];
    const inPayloads = mergedRows.filter((row) => row.direction === 'in').map((row) => row.payload);
    starterHeadings = starterGroupHeadings(inPayloads);
    // The last starter interest tag carries the "Create my tag" prompt, just above the Tag Guide demo.
    lastStarterKey = String([...inPayloads].reverse()
      .find((cluster: any) => cluster?.starterContactId === STARTER_TAGS_CONTACT_ID)?.identityKey || '');
    const renderMergedRow = (row: MergedTalkRow): string =>
      row.direction === 'divider'
        ? `<div class="talks-direction-divider" data-testid="talks-direction-divider" role="separator"><span>${escapeHtml(deps.t('talksOutgoingSection'))}</span></div>`
        : row.direction === 'out' ? renderOutRow(row.payload) : renderInRow(row.payload);

    // mergedRows is guaranteed non-empty here — the outer `filteredOutEntries.length
    // === 0 && inEntries.length === 0` check above already handled the true-empty case.
    const hasTagChips = mergedRows.some((row) => String(row.payload?.type || row.payload?.[1]?.type || '').toLowerCase() === 'tag');
    renderListProgressively(talksList, mergedRows, {
      firstChunkSize: TALKS_FIRST_CHUNK_SIZE,
      renderRow: renderMergedRow,
      prefixHtml: hasTagChips ? `<div class="talks-tap-hint" data-testid="talks-tap-hint">${escapeHtml(deps.t('talksTapHint'))}</div>` : '',
      onFirstChunkRendered: () => { flowTagChips(talksList); reapplyTalkRowSelection(); },
      onRemainderRendered: () => { flowTagChips(talksList); reapplyTalkRowSelection(); },
      isStale,
    });

    // Request stats for out talks (created/copied) only
    if (filteredOutEntries.length > 0) {
      const talkIds = filteredOutEntries.map(([id]) => id);
      deps.emit('needTalkStats', { talkIds });
    }

    // TODO §R2: delegated (bound once) — replaces two per-render listener-binding loops
    // so a row landing in renderListProgressively's deferred remainder is interactive
    // immediately, with nothing to (re-)attach. `getMyTalks()` is re-read at click time
    // (not closed over), so a stale snapshot from an earlier render can't be used either.
    if (!documentState.clickDelegationBound) {
      documentState.clickDelegationBound = true;
      // Delegated on body, not #talks-list: the sender/matched-people click-to-DM
      // affordance (§N3) now also lives inside `.talk-item-details`, which the details
      // popup relocates to document.body when opened (showDetailsPopupFor) — a listener
      // scoped to #talks-list would stop catching it once moved. The row-click-to-edit
      // branch below is unaffected: it requires a `.talk-list-item` ancestor, which
      // popup content never has once relocated, so it naturally no-ops there.
      // Keyboard: Enter/Space taps a tag; Delete/Backspace passes it.
      document.body.addEventListener('keydown', (e) => {
        const chip = (e.target as HTMLElement | null)?.closest?.('#talks-list .talk-tag-chip') as HTMLElement | null;
        if (!chip) return;
        const currentDeps = documentState.latestDeps || deps;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          actOnTagRow(chip, 'tap', currentDeps);
        } else if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          actOnTagRow(chip, 'pass', currentDeps);
        }
      });
      document.body.addEventListener('click', (e) => {
        // A row-drag gesture (ignore/copy/delete) or a long-press-for-details just
        // committed or cancelled — the click that naturally follows pointerup should
        // not also open the talk.
        const currentDeps = documentState.latestDeps || deps;
        if (Date.now() < currentDeps.getTalksGestureSuppressClickUntil()) return;
        const target = e.target as HTMLElement;

        // AppBar ✕ Pass · ♡ Like · 📌 Pin act on the long-pressed row (tag-tap-mode.ts).
        const toolbarButton = target.closest('.talks-tap-mode-btn') as HTMLButtonElement | null;
        if (toolbarButton) {
          e.preventDefault();
          const action = toolbarButton.id.replace('talks-tap-', '').replace('-btn', '') as TalkToolbarAction;
          actOnSelectedRow(action, currentDeps);
          return;
        }
        if (target.closest('#talks-list')) clearTalkRowSelection();
        // A plain tap on a tag acts at once (see actOnTagRow).
        const tappedChip = target.closest('#talks-list .talk-tag-chip') as HTMLElement | null;
        if (tappedChip) {
          e.preventDefault();
          e.stopPropagation();
          actOnTagRow(tappedChip, 'tap', currentDeps);
          return;
        }

        if (target.closest('[data-testid="starter-tag-create-btn"]')) {
          e.preventDefault();
          e.stopPropagation();
          // A new Talk opens as a tag (talk-editor-dialog.ts defaults to it).
          document.getElementById('create-talk-btn')?.click();
          return;
        }

        const restoreButton = target.closest('.talk-restore-ignored-btn') as HTMLElement | null;
        if (restoreButton) {
          e.preventDefault();
          e.stopPropagation();
          const talkId = restoreButton.dataset.talkId || '';
          if (talkId) currentDeps.restoreIgnoredTalk(talkId);
          return;
        }

        const copyButton = target.closest('.talk-copy-button') as HTMLElement | null;
        if (copyButton) {
          e.preventDefault();
          e.stopPropagation();
          const talkId = copyButton.dataset.talkId || '';
          if (getMyTalks()[talkId]?.role === 'copied') {
            uncopyIncomingTalk(talkId, {
              showNotification: (message, type) => currentDeps.showNotification(message, type),
              t: currentDeps.t,
              refreshTalksList: () => displayTalksList(currentDeps),
            });
          }
          else currentDeps.quickCopyIncomingTalk(talkId, copyButton.dataset.identityKey || undefined);
          return;
        }

        const viewButton = target.closest('.view-talk-btn') as HTMLElement | null;
        if (viewButton) {
          e.preventDefault();
          e.stopPropagation();
          const talkId = viewButton.dataset.talkId || '';
          const identityKey = viewButton.dataset.identityKey || '';
          if (talkId || identityKey) currentDeps.showTalkDetail(talkId, identityKey || undefined);
          return;
        }

        // TODO §N3: trace back from a talk row to whom it was exchanged with, then DM
        // them. Single exchange partner navigates straight through the dispatcher;
        // multiple partners opens the "choose who to DM" picker.
        const peopleEl = target.closest('.talk-matched-people, .talk-sender-people') as HTMLElement | null;
        if (peopleEl) {
          e.stopPropagation();
          let people: Array<{ id: string; name: string }> = [];
          try {
            people = JSON.parse(peopleEl.dataset.matchedPeople || peopleEl.dataset.senderPeople || '[]');
          } catch {
            return;
          }
          if (people.length === 1) {
            deps.navigateToGraphNode({ type: 'person', id: people[0].id, name: people[0].name });
          } else if (people.length > 1) {
            deps.showChooseWhoToDmPicker(people);
          }
          return;
        }

        // Row click opens edit/detail only when not clicking an action button (handled
        // in the mousedown-capture delegation above).
        if (target.closest('.talk-item-actions, .talk-item-inline-actions, .talk-tag-decision-actions, .talk-icon-badge, .view-talk-btn, .talk-copy-button, .talk-matched-people, .talk-sender-people, .talk-item-details')) return;
        const item = target.closest('.talk-list-item') as HTMLElement | null;
        if (!item) return;
        const talkId = item.dataset.talkId || '';
        const identityKey = item.dataset.identityKey || '';
        const role = item.dataset.role;
        if (role === 'incoming' && !talkId && !identityKey) return;
        if (role !== 'incoming' && !talkId) return;
        if (role === 'copied') {
          const copied = getMyTalks()[talkId];
          if (copied?.fullTalk) {
            // docs/TODO.md §Y1: pass the talk as-is (original authorship intact) — the
            // editor dialog itself decides create-vs-update-in-place by comparing
            // existingTalk.authorId to currentUserId. Pre-stamping here would make every
            // copied talk look self-authored before the user has actually edited anything.
            deps.showTalkEditorDialog(copied.fullTalk);
          } else {
            deps.showNotification(deps.t('talksCouldNotLoad'), 'error');
          }
        } else if (role === 'created') {
          deps.emit('loadTalkForEdit', { talkId });
        } else {
          deps.showTalkDetail(talkId, identityKey || undefined);
        }
      });
    }
  }

  // Restore the remembered scroll "spot" for this tab — deferred a tick so it applies
  // after the just-rendered content lands. `.talks-list`'s own `overflow-y:auto` never
  // actually engages (its flex parent, `.view-content`, isn't itself a flex container),
  // so `#talks-view-content` — not `#talks-list` — is the element that really scrolls.
  const talksScrollContainer = document.getElementById('talks-view-content');
  if (talksScrollContainer) {
    if (talksScrollContainer.dataset.talksScrollRestored !== '1') {
      talksScrollContainer.dataset.talksScrollRestored = '1';
      try {
        const raw = settingsStorage.getItem(TALKS_TAB_STATE_KEY);
        const savedScrollTop = raw ? (JSON.parse(raw) as { scrollTop?: number }).scrollTop : undefined;
        if (typeof savedScrollTop === 'number') {
          window.setTimeout(() => { talksScrollContainer.scrollTop = savedScrollTop; }, 0);
        }
      } catch {
        /* local-only preference persistence is optional */
      }
    }
    if (talksScrollContainer.dataset.talksScrollBound !== '1') {
      talksScrollContainer.dataset.talksScrollBound = '1';
      talksScrollContainer.addEventListener('scroll', () => deps.persistTalksTabState(), { passive: true });
    }
  }

  deps.syncStatusBarMatchCount();
}
